import { DefaultButton, Label, PrimaryButton, Spinner } from "@fluentui/react"
import { connect } from "react-redux";
import { useBoolean } from '@fluentui/react-hooks';

import ContractStageInitialFormComponent from "../StageInitialForm/StageInitialForm.Component";
import ContractStageFormComponent from "../StageForm/StageForm.Component";

// functions
import * as ComponentFunctions from './StageList.Functions';

////i18Next
import { useTranslation } from 'react-i18next';

// styles
import './StageList.Styles.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DragPreviewInfo, StageItemComponent } from "./StageItem.Component";
import { ContractStage } from "../../../../models/Entities/Contracts/ContractStage";
import { ErrorMessageComponent } from "../../../Error/ErrorMessageComponent";
import { Tab, TabList } from "@fluentui/react-components";
import { Branch16Regular, CircleOff16Regular } from "@fluentui/react-icons";


/* The preview is the same size as the card, so the raw grab offset keeps the
   copy exactly under the point where it was picked up. */
const previewTransform = (x: number, y: number, offsetX: number, offsetY: number): string =>
    `translate3d(${x - offsetX}px, ${y - offsetY}px, 0)`;

type DragPreviewState = DragPreviewInfo;

interface BranchTab {
    key: string;
    label: string;
    branchId?: number;
    count: number;
}

export const StageListComponent = (props: ComponentFunctions.Props) => {

    // initial values
    const [isOpenInitialSettings, { setTrue: openPanelInitialSettings, setFalse: dismissPanelInitialSettings }] = useBoolean(false);
    const [selectedStage, setSelectedStage] = useState<ContractStage | undefined>(undefined);

    // Add or eddit visit
    const [isOpenVisit, { setTrue: openPanelVisit, setFalse: dismissPanelVisit }] = useBoolean(false);

    // drag & drop reorder
    const [localStages, setLocalStages] = useState<ContractStage[]>([]);
    const [dragStageId, setDragStageId] = useState<number | null>(null);
    const [overStageId, setOverStageId] = useState<number | null>(null);
    const [dragPreview, setDragPreview] = useState<DragPreviewState | null>(null);
    const dragPreviewRef = useRef<HTMLDivElement | null>(null);

    // tabs
    const [selectedTab, setSelectedTab] = useState<string>('unassigned');

    //useTranslation()
    const [t] = useTranslation();

    const handleInitialSettingsClosePanel = () => {
        dismissPanelInitialSettings();
    };

    const handleVisitClosePanel = () => {
        setSelectedStage(undefined);
        dismissPanelVisit();
    };

    const handleChargeVisitInformation = () => {
        openPanelInitialSettings();
    };

    const HandleAddEvent = () => {
        openPanelVisit();
    };

    const HandleEditEvent = (stage: ContractStage) => {
        setSelectedStage(stage);
        openPanelVisit();
    }

    const handleDragStart = (stageId: number, preview: DragPreviewInfo) => {
        setDragStageId(stageId);
        setDragPreview(preview);
    };

    /*
     * Places the cloned card inside the floating wrapper and puts it where the
     * drag started. The transform is written to the node instead of being a style
     * prop so that a re-render mid drag (the insertion line moving, for one) does
     * not snap the preview back to its initial position.
     */
    const attachDragPreview = useCallback((wrapper: HTMLDivElement | null) => {
        dragPreviewRef.current = wrapper;
        if (!wrapper || !dragPreview) return;
        if (dragPreview.node && wrapper.childElementCount === 0) {
            wrapper.appendChild(dragPreview.node);
        }
        wrapper.style.transform = previewTransform(dragPreview.x, dragPreview.y, dragPreview.offsetX, dragPreview.offsetY);
    }, [dragPreview]);

    /*
     * The native drag image is transparent by definition, so the item hands the
     * browser an empty pixel and the card that follows the cursor is this one,
     * painted fully opaque. dragover on the document is what reports the pointer
     * while a drag is in flight (mousemove does not fire during a drag), and the
     * position is written straight to the node: it fires dozens of times per
     * second and re-rendering the whole list on each one is not worth it.
     */
    useEffect(() => {
        if (!dragPreview) return;

        const { offsetX, offsetY } = dragPreview;
        const trackPointer = (event: DragEvent) => {
            if (event.clientX === 0 && event.clientY === 0) return;
            const node = dragPreviewRef.current;
            if (node) node.style.transform = previewTransform(event.clientX, event.clientY, offsetX, offsetY);
        };

        document.addEventListener('dragover', trackPointer);
        return () => document.removeEventListener('dragover', trackPointer);
    }, [dragPreview !== null]);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>, stageId: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (overStageId !== stageId) setOverStageId(stageId);
    };

    // leaving the list altogether clears the insertion line
    const handleListDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOverStageId(null);
    };

    const handleDrop = (targetStageId: number) => {
        setDragPreview(null);
        if (dragStageId === null || dragStageId === targetStageId) {
            setDragStageId(null);
            setOverStageId(null);
            return;
        }

        const sourceIndex = localStages.findIndex((s) => s.id === dragStageId);
        const targetIndex = localStages.findIndex((s) => s.id === targetStageId);

        if (sourceIndex < 0 || targetIndex < 0) {
            setDragStageId(null);
            setOverStageId(null);
            return;
        }

        const reordered = [...localStages];
        const [moved] = reordered.splice(sourceIndex, 1);
        reordered.splice(targetIndex, 0, moved);
        const withNewOrder = reordered.map((s, i) => ({ ...s, stageOrder: i + 1 }));
        setLocalStages(withNewOrder);

        // dispatch only stages whose order actually changed
        withNewOrder.forEach((newS) => {
            const original = localStages.find((o) => o.id === newS.id);
            if (original && original.stageOrder !== newS.stageOrder) {
                props.UpdateContractStage(newS);
            }
        });
        setDragStageId(null);
        setOverStageId(null);
    };

    const handleDragEnd = () => {
        setDragStageId(null);
        setOverStageId(null);
        setDragPreview(null);
    };

    useEffect(() => {
        setLocalStages(props.Stages ?? []);
    }, [props.Stages]);

    /*
     * Side of the hovered card the dragged visit will land on. handleDrop removes
     * the source before inserting at the target index, so dragging downwards drops
     * after the target and dragging upwards drops before it. The line follows that
     * same rule instead of just highlighting the card underneath.
     */
    const dropPosition = useMemo<'before' | 'after' | null>(() => {
        if (dragStageId === null || overStageId === null || dragStageId === overStageId) return null;
        const sourceIndex = localStages.findIndex((stage) => stage.id === dragStageId);
        const targetIndex = localStages.findIndex((stage) => stage.id === overStageId);
        if (sourceIndex < 0 || targetIndex < 0) return null;
        return sourceIndex < targetIndex ? 'after' : 'before';
    }, [dragStageId, overStageId, localStages]);

    const knownBranchIds = useMemo(
        () => new Set((props.Branches || []).map((branch) => branch.id)),
        [props.Branches]
    );

    // what the branch badge of a visit shows, for the branches still in the contract
    const branchNameById = useMemo(
        () => new Map((props.Branches || []).map((branch) => [branch.id, branch.branchName])),
        [props.Branches]
    );

    // Deleting a branch leaves the visits that pointed at it with a dangling branchId. Those visits
    // are listed under "Sin rama" instead of getting one tab per branch that no longer exists.
    const isUnassignedStage = useCallback(
        (stage: ContractStage): boolean =>
            stage.branchId === undefined || stage.branchId === null || !knownBranchIds.has(stage.branchId),
        [knownBranchIds]
    );

    // While the addendum branches haven't been fetched an unknown branchId can't be told apart from
    // a branch that simply isn't loaded, so nothing is flagged as orphan until then.
    const isOrphanBranchId = useCallback(
        (branchId?: number | null): boolean =>
            props.AreBranchesLoaded
            && branchId !== undefined
            && branchId !== null
            && !knownBranchIds.has(branchId),
        [knownBranchIds, props.AreBranchesLoaded]
    );

    const branchTabs = useMemo<BranchTab[]>(() => {
        const countByBranch = new Map<string, number>();
        (localStages || []).forEach((stage) => {
            const key = isUnassignedStage(stage) ? 'unassigned' : `branch-${stage.branchId}`;
            countByBranch.set(key, (countByBranch.get(key) || 0) + 1);
        });

        const knownTabs = (props.Branches || []).map((branch) => ({
            key: `branch-${branch.id}`,
            label: branch.branchName,
            branchId: branch.id,
            count: countByBranch.get(`branch-${branch.id}`) || 0
        }));

        return [
            { key: 'unassigned', label: t('stageList.props.unassignedBranch'), count: countByBranch.get('unassigned') || 0 },
            ...knownTabs
        ];
    }, [localStages, props.Branches, isUnassignedStage, t]);

    useEffect(() => {
        if (!branchTabs.some((tab) => tab.key === selectedTab)) {
            setSelectedTab('unassigned');
        }
    }, [branchTabs, selectedTab]);

    const filteredStages = useMemo(() => {
        if (selectedTab === 'unassigned') {
            return localStages.filter((stage) => isUnassignedStage(stage));
        }

        const branchId = Number.parseInt(selectedTab.replace('branch-', ''), 10);
        if (Number.isNaN(branchId)) {
            return localStages;
        }

        return localStages.filter((stage) => stage.branchId === branchId);
    }, [localStages, selectedTab, isUnassignedStage]);

    useEffect(() => {
        props.GetContractById(props.ContractId);
    }, [props.ContractId !== undefined]);

    // efects   
    /* Keyed on the contract id, not on the contract object: any reducer that
     * rewrites the contract (the import status poll, for one) would otherwise
     * refetch the stages of the addendum over and over. */
    useEffect(() => {
        if (props.Contract) {
            props.GetAllContractStagesByAddendumId(props.ContractId, props.ContractAddendumId);
        }
    }, [props.Contract?.id, props.ContractAddendumId]);

    // the branches of the addendum are what tells a live branch from a deleted one
    useEffect(() => {
        if (props.Contract?.id && props.ContractAddendumId) {
            props.GetContractBranchsByAddendumId(props.ContractId, props.ContractAddendumId);
        }
    }, [props.Contract?.id, props.ContractAddendumId]);

    // Loading
    const Loading = (props.isLoading) && (
        <div>
            <Spinner
                label={t('stageList.props.loadingVisits')}
                ariaLive="assertive"
                labelPosition="top"
            />
        </div>
    );

    const ComponentActions = (
        <PrimaryButton
            secondaryText={t('stageList.props.addVisits')}
            onClick={HandleAddEvent}
            text={t('stageList.props.addVisits')}
            iconProps={{ iconName: 'Add' }}
            className="actionButton"
        />
    )

    const ErrorMessage = (props.failOnLoading) && props.error && (
        <ErrorMessageComponent Error={props.error} />
    )

    const NoRegistries = (localStages?.length === 0 || localStages?.length === undefined) && (
        <div className="NoDataSection">
            <img
                src={require('../../../../assets/img/empty_state.gif')}
                alt="Ichtys"
                className="sl-empty-illustration"
            ></img>
            <Label className="sl-empty-title">{t('stageList.props.registeredVisits')}</Label>
            <DefaultButton
                text={t('stageList.props.loadingVisitInformation')}
                iconProps={{ iconName: 'Add' }}
                className="sl-empty-action"
                onClick={handleChargeVisitInformation} />
        </div>
    );

    const NoRegistriesByTab = localStages.length > 0 && filteredStages.length === 0 && (
        <div className="NoDataByTabSection">
            <Label>{t('stageList.props.noVisitsForSelectedBranch')}</Label>
        </div>
    );

    const Forms = <>
        <ContractStageInitialFormComponent
            isOpen={isOpenInitialSettings}
            openPanel={openPanelInitialSettings}
            dismissPanel={handleInitialSettingsClosePanel}
            ContractId={props.ContractId}
            AddendumId={props.ContractAddendumId}
        />
        <ContractStageFormComponent
            isOpen={isOpenVisit}
            openPanel={openPanelVisit}
            dismissPanel={handleVisitClosePanel}
            ContractId={props.ContractId}
            ContractAddendumId={props.ContractAddendumId}
            ProtocolId={props.Contract?.protocol?.[0]?.protocolId}
            ContractStageId={selectedStage?.id}
            CurrentCycles={props.Cycles}
        />
    </>


    return (
        <div className="sl-shell">
            <div className="sl-toolbar">
                <div className="sl-toolbar-actions">{ComponentActions}</div>
            </div>
            {ErrorMessage}
            {props.isLoading ? <div className="sl-loading">{Loading}</div> : null}
            {localStages.length > 0 && (
                <TabList
                    className="sl-tabs"
                    selectedValue={selectedTab}
                    onTabSelect={(_, data) => setSelectedTab(String(data.value))}
                >
                    {branchTabs.map((tab) => (
                        <Tab key={tab.key} value={tab.key}>
                            <span className="sl-tab-content" title={tab.label}>
                                <span className="sl-tab-icon" aria-hidden="true">
                                    {tab.branchId === undefined ? <CircleOff16Regular /> : <Branch16Regular />}
                                </span>
                                <span className="sl-tab-label">{tab.label}</span>
                                <span className={tab.count === 0 ? 'sl-tab-badge sl-tab-badge--empty' : 'sl-tab-badge'}>
                                    {tab.count}
                                </span>
                            </span>
                        </Tab>
                    ))}
                </TabList>
            )}
            <div className="si-list" onDragLeave={handleListDragLeave}>
                {filteredStages.map((stage, index) => (
                    <StageItemComponent
                        key={stage.id}
                        Stage={stage}
                        index={index}
                        displayOrder={index + 1}
                        ProtocolId={props.Contract?.protocol?.[0]?.protocolId}
                        onEdit={(stage: ContractStage) => HandleEditEvent(stage)}
                        onDelete={(stage: ContractStage) => props.DeleteContractStage(props.ContractId, props.ContractAddendumId, stage.id)}
                        onDetachBranch={(stage: ContractStage) => props.DetachContractStageBranch(props.ContractId, props.ContractAddendumId, stage.id)}
                        isDeleting={props.isDeleting}
                        failOnDeleting={props.isDeletedSuccessfully}
                        error={props.error}
                        isOrphanBranch={isOrphanBranchId(stage.branchId)}
                        branchName={stage.branchId !== undefined && stage.branchId !== null ? branchNameById.get(stage.branchId) : undefined}
                        orphanBranchId={stage.branchId}
                        isDragging={dragStageId === stage.id}
                        isDragOver={overStageId === stage.id && dragStageId !== stage.id}
                        dropPosition={overStageId === stage.id ? dropPosition : null}
                        onDragStart={(_, preview) => handleDragStart(stage.id, preview)}
                        onDragOverCard={(e) => handleDragOver(e, stage.id)}
                        onDrop={() => handleDrop(stage.id)}
                        onDragEnd={handleDragEnd}
                    />
                ))}
            </div>
            {NoRegistriesByTab}
            {NoRegistries}
            {Forms}
            {dragPreview && createPortal(
                <div
                    ref={attachDragPreview}
                    className="si-drag-preview"
                    aria-hidden="true"
                    style={{ width: dragPreview.width || undefined }}
                />,
                document.body
            )}
        </div>
    )
}

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(StageListComponent as any);

